import { useCallback, useState } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import useApiDelete from 'Pods/Shared/Hooks/useApiDelete'
import useApiPost from 'Pods/Shared/Hooks/useApiPost'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'

const MODERATION_RUNS_ENDPOINT = '/api/internal/generator/moderation/runs'

export const MODERATION_ACTION = { DELETE: 'delete', HIDE: 'hide' }

const showNotification = (message, type) => {
  if (window.showNotification) {
    window.showNotification(message, { type, autohide: true })
  }
}

const requestPath = ({ action, image }) => (
  action === MODERATION_ACTION.DELETE
    ? `${MODERATION_RUNS_ENDPOINT}/${image.id}`
    : `${MODERATION_RUNS_ENDPOINT}/${image.id}/nsfw-flag`
)

// Both actions take the run out of the public gallery (a hidden run is forced private),
// so the card leaves every cached filter combination at once rather than waiting on a
// refetch of the five-minute server-side index. Entries without the run keep their
// identity so unrelated cards do not re-render.
const removeFromGalleryCache = (queryClient, id) => {
  queryClient.setQueriesData({ queryKey: ['communityGalleryImages'] }, (cached) => {
    if (!cached?.pages) return cached

    let changed = false
    const pages = cached.pages.map((page) => {
      const items = page.items.filter(item => item.id !== id)
      if (items.length === page.items.length) return page
      changed = true
      return { ...page, items }
    })

    return changed ? { ...cached, pages } : cached
  })
}

// Admin moderation of a gallery image: a request is held as `pending` until the confirm
// modal settles it, then DELETE (remove the run) or POST (flag it NSFW) is sent.
function useImageModeration() {
  const t = useTranslation()
  const queryClient = useQueryClient()
  const { deleteData, isLoading: isDeleting } = useApiDelete()
  const { postData, isLoading: isHiding } = useApiPost()
  const [pending, setPending] = useState(null)

  const requestDelete = useCallback((image) => {
    setPending({ action: MODERATION_ACTION.DELETE, image })
  }, [])
  const requestHide = useCallback((image) => {
    setPending({ action: MODERATION_ACTION.HIDE, image })
  }, [])
  const cancel = useCallback(() => setPending(null), [])

  const confirm = useCallback(() => {
    if (!pending) return

    const send = pending.action === MODERATION_ACTION.DELETE ? deleteData : postData

    send({
      path: requestPath(pending),
      onSuccess: () => {
        removeFromGalleryCache(queryClient, pending.image.id)
        showNotification(t(`imageGallery.moderation.${pending.action}.success`), 'success')
        setPending(null)
      },
      onError: () => {
        showNotification(t(`imageGallery.moderation.${pending.action}.failed`), 'error')
        setPending(null)
      },
    })
  }, [pending, deleteData, postData, queryClient, t])

  return {
    pending,
    isSubmitting: isDeleting || isHiding,
    requestDelete,
    requestHide,
    confirm,
    cancel,
  }
}

export default useImageModeration
